'use client';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { MetaProps, LinkProps } from '@repo/ui/src/types';
import { classNames } from '@repo/ui/src/utils/classNames';
import Image from '@repo/ui/src/components/Image/New';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { appConfig } from '../../utils/appConfig';

export interface LinkWithIconProps {
  _meta: MetaProps;
  displayName: string;
  externalLink: string;
  imageContent: any;
  isExternal: boolean;
  link: string;
  noFollow: boolean;
  noIndex: boolean;
  anchorTag?: string;
}

export interface LinkGroupProps {
  _meta: MetaProps;
  links: Array<LinkWithIconProps>;
  title?: string;
  titleLink?: LinkProps;
  primaryHeader?: string;
  secondaryHeader?: string;
}

export const LinkGroup = ({ links, primaryHeader, secondaryHeader }: LinkGroupProps) => {
  if (links?.length === 0) return null;
  const hasTwo = links?.length === 2;

  return (
    <ul
      className={classNames(
        'grid px-6 gap-y-6 gap-x-6 md:pl-0 list-none',
        hasTwo ? 'sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : 'sm:grid-cols-3',
      )}
    >
      {links?.map(
        (
          {
            link,
            imageContent,
            displayName,
            isExternal,
            noFollow,
            noIndex,
            externalLink,
            anchorTag,
          },
          i,
        ) => (
          <li key={i}>
            <Anchor
              className="grid grid-cols-2 gap-x-4 sm:flex flex-col group/link-hover"
              link={link}
              isExternal={isExternal}
              externalLink={externalLink}
              noFollow={noFollow}
              noIndex={noIndex}
              anchorTag={anchorTag}
              onClick={() =>
                navigationClicked({
                  nav_type: 'Top Navigation',
                  clicked_text: displayName ?? '',
                  primary_header: primaryHeader ?? '',
                  secondary_header: secondaryHeader ?? '',
                  tertiary_header: displayName ?? '',
                })
              }
            >
              <div className="block h-auto gap-x-4 cursor-pointer aspect-video sm:aspect-square max-h-48">
                {imageContent && (
                  <div
                    className={classNames(
                      'relative h-full overflow-hidden',
                      appConfig.isTraeger && 'rounded',
                    )}
                  >
                    <Image
                      src={buildImageSrc(imageContent?.image, 300)}
                      alt={imageContent?.image?.accessibilityLabel || displayName}
                      className={classNames(
                        'h-full sm:w-auto object-cover object-center group-hover/link-hover:scale-150 transition-all group-hover/link-hover:duration-[5000ms] group-hover/link-hover:ease-in ease-out duration-1000',
                        hasTwo ? 'w-full h-[240px]' : 'aspect-square',
                      )}
                      fill
                    />
                  </div>
                )}
              </div>
              <p
                className={classNames(
                  'text-neutral-700 self-center md:self-start group-hover/link-hover:text-brand-400 group-hover/link-hover:underline transition-all duration-300',
                  appConfig.isMeater && 'tra-text-web-heading-4 pt-5 !normal-case',
                  appConfig.isTraeger && 'tra-text-web-sub-3 pt-2',
                )}
              >
                {displayName}
              </p>
            </Anchor>
          </li>
        ),
      )}
    </ul>
  );
};

export default LinkGroup;
